import React, { useContext, useEffect, useState } from "react";
import { Title } from "../../../../../stories/Title/Title";
import { Button } from "../../../../../stories/Button/Button";
import { useNavigate } from "react-router-dom";
import {
  LicenseTypes,
  Operation,
  PageFor,
} from "../../../../../utils/Enum.types";
import { IObjectKeys } from "../../../../../utils/formTypes";
import { Form, Formik, FormikProps } from "formik";
import { categoryValidation } from "../../../../../utils/validationSchema";
import { AddTicketReason, UpdateTicketReason } from "./queries/mutations";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  AddStockCategoryResultResult,
  AddStockCategoryResultVar,
  AtsTicketReasons,
  TicketPriorityType,
  UpdateTicketReasonResult,
  UpdateTicketReasonVar,
} from "./queries/types";
import { emptyMessage } from "../../../../../utils/constants";
import useUrlParams from "../../../../../customHooks/useUrlParams";
import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";
import { GetTicketReasons, GetTicketReasonsByNodeId } from "./queries/queries";
import { AppContext } from "../../../../../context/context";
import MessageModal from "../../../../../customComponents/MessageModal";
import { msgType } from "../../../../../utils/types";
import { MasterDataProps } from "../itemgroup/Index";
import Close from "../../../../../images/Close.svg";
import LoadingModal from "../../../../pages/LoadingModal";
import Navbar from "../../../../common/layout/Navbar";
import List from "./List";
import Add from "./Add";
import { nodeVar } from "../../../../../utils/commonQueryTypes";

interface formDataType extends IObjectKeys {
  id: number;
  reason: string;
  priority: TicketPriorityType;
}

export interface CommonProps extends MasterDataProps {
  setOperation: React.Dispatch<React.SetStateAction<Operation>>;
  handleClear?: any;
}
export interface AddMasterDataProps extends CommonProps {
  formData: formDataType;
  handleValueChange: (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    meta: FormikProps<formDataType>
  ) => void;
  meta: FormikProps<formDataType>;
  operation: Operation;
  setFormData: React.Dispatch<React.SetStateAction<formDataType>>;
}

export interface ListPageProps extends CommonProps {
  setMessage: React.Dispatch<React.SetStateAction<msgType>>;
}

const TicketReason = ({ type, setModal }: MasterDataProps) => {
  const navigate = useNavigate();
  const [message, setMessage] = useState(emptyMessage);
  const { user_details } = useLoggedInUserDetails();
  const { state } = useContext(AppContext);
  const [operation, setOperation] = useState(Operation.CREATE);
  const { customer_id } = useUrlParams();
  const [formData, setFormData] = useState<formDataType>({
    id: 0,
    priority: TicketPriorityType.LOW,
    reason: "",
  });

  const [getTicketReasonsByNodeId, { loading }] = useLazyQuery<
    { node: AtsTicketReasons },
    nodeVar
  >(GetTicketReasonsByNodeId);

  const [addTicketReason, { loading: AddLoading }] = useMutation<
    AddStockCategoryResultResult,
    AddStockCategoryResultVar
  >(AddTicketReason, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  const [updateTicketReason, { loading: UpdateLoading }] = useMutation<
    UpdateTicketReasonResult,
    UpdateTicketReasonVar
  >(UpdateTicketReason, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  const handleValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    meta: FormikProps<formDataType>
  ) => {
    const { name, value } = e.target as HTMLInputElement;

    setFormData(() => ({
      ...formData,
      [name]: value,
    }));
    meta.handleChange(e);
  };
  const handleClear = () => {
    const inputElement = document.getElementsByName(
      "reason"
    )[0] as HTMLInputElement;
    if (inputElement) inputElement.focus();
    setFormData({
      id: 0,
      priority: TicketPriorityType.LOW,
      reason: "",
    });
  };

  const handleSubmit = async () => {
    if (customer_id) {
      if (operation === Operation.CREATE) {
        const empAdded = addTicketReason({
          variables: {
            user_details,
            customer_id,
            input: {
              priority: formData.priority,
              reason: formData.reason,
            },
          },
          refetchQueries: [
            {
              query: GetTicketReasons,
              variables: {
                customer_id,
              },
            },
          ],
        });
        const { data } = await empAdded;

        if (data && data.AddTicketReason) {
          setMessage({
            flag: true,
            message: "Ticket reason added successfully",
            operation: Operation.CREATE,
          });
        }
      } else {
        const customerUpdated = updateTicketReason({
          variables: {
            user_details,
            reasons_id: formData.id,
            input: {
              priority: formData.priority,
              reason: formData.reason,
            },
          },
          refetchQueries: [
            {
              query: GetTicketReasons,
              variables: {
                customer_id,
              },
            },
          ],
        });
        const { data } = await customerUpdated;

        if (data && data.UpdateTicketReason) {
          setMessage({
            flag: true,
            message: "Ticket reason details updated successfully",
            operation: Operation.CREATE,
          });
        }
      }
    }
  };

  const hanldeClear = () => {
    setFormData({
      id: 0,
      priority: TicketPriorityType.LOW,
      reason: "",
    });
  };
  const handleClose = () => {
    if (message.operation !== Operation.NONE) {
      hanldeClear();
      setOperation(Operation.CREATE);
    }
    setMessage(emptyMessage);
  };

  useEffect(() => {
    if (state.ticketReasonId && operation === Operation.UPDATE) {
      getTicketReasonsByNodeId({
        variables: {
          id: state.ticketReasonId,
        },
      }).then(({ data }) => {
        if (data && data.node) {
          setFormData({
            id: data.node.id,
            priority: data.node.priority,
            reason: data.node.reason,
          });
        }
      });
    }
  }, [GetTicketReasonsByNodeId, operation, state.ticketReasonId]);

  return (
    <>
      {type === PageFor.GENERAL && (
        <Navbar NavType={LicenseTypes.CUSTOMER_USERS} />
      )}
      <div className="master-data--modal--title">
        <Title>Ticket Reason</Title>
        {type === PageFor.MODAL && (
          <img src={Close} alt="" onClick={() => setModal?.(false)} />
        )}
      </div>

      <Formik
        initialValues={formData}
        validationSchema={categoryValidation}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnChange
      >
        {(meta) => {
          return (
            <Form
              className={`master-data${
                type === PageFor.MODAL ? "--modal" : ""
              }`}
            >
              <div
                className={`master-data__datablock${
                  type === PageFor.MODAL ? "--modal" : ""
                }`}
              >
                <div className="master-data__datablock--left">
                  <Add
                    type={PageFor.GENERAL}
                    formData={formData}
                    handleValueChange={handleValueChange}
                    setOperation={setOperation}
                    meta={meta}
                    operation={operation}
                    setFormData={setFormData}
                  />
                </div>
                <div className="master-data__datablock--right">
                  <List
                    type={PageFor.GENERAL}
                    setOperation={setOperation}
                    setMessage={setMessage}
                  />
                </div>
              </div>
              <Button
                mode="save"
                onClick={() => {
                  handleSubmit();
                }}
                className="submit"
                type="submit"
              />
              <Button mode="clear" type="button" onClick={handleClear} />
              {type === PageFor.GENERAL ? (
                <Button
                  mode="back"
                  type="button"
                  onClick={() => navigate(-1)}
                />
              ) : (
                <Button
                  mode="cancel"
                  type="button"
                  onClick={() => setModal?.(false)}
                />
              )}
            </Form>
          );
        }}
      </Formik>
      <MessageModal
        handleClose={handleClose}
        modalFlag={message.flag}
        operation={message.operation}
        value={message.message}
      />
      <LoadingModal flag={loading || AddLoading || UpdateLoading} />
    </>
  );
};

export default TicketReason;
